import { useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, Calendar, ExternalLink, Loader2 } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

const OWNER_ID = process.env.REACT_APP_ACUITY_OWNER_ID || "";

function buildAcuityUrl({ appointmentType, category } = {}) {
  if (!OWNER_ID) return null;
  const params = new URLSearchParams({ owner: OWNER_ID, ref: "embedded_csp" });
  if (appointmentType) params.set("appointmentType", String(appointmentType));
  if (category) params.set("category", category);
  return `https://app.acuityscheduling.com/schedule.php?${params.toString()}`;
}

// Themed lightbox wrapping the Acuity scheduler. Keeps the cinematic USDD
// frame around the booking engine so the Acuity UI never breaks the visual
// language of the site. Renders via portal to escape any parent overflow.
export const BookingModal = ({
  open,
  onClose,
  title = "Reserve your spot",
  subtitle = null,
  appointmentType,
  category,
  bookingUrl,
}) => {
  // Per-slot override wins over the auto-built URL
  const src = bookingUrl || buildAcuityUrl({ appointmentType, category });

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    trackEvent("booking_modal_open", { title, appointmentType, category });
    const onKey = (e) => { if (e.key === "Escape") handleClose(); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, handleClose, title, appointmentType, category]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      data-testid="booking-modal"
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={handleClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/85 backdrop-blur-md" />

      {/* Panel */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-3xl max-h-[92vh] rounded-t-3xl sm:rounded-3xl overflow-hidden border border-brand-yellow/30 bg-gradient-to-b from-brand-purpleDeep/40 via-zinc-950 to-black shadow-[0_30px_120px_rgba(0,0,0,0.75)] flex flex-col"
      >
        {/* Header */}
        <div className="relative flex items-center justify-between gap-4 px-5 sm:px-7 py-4 border-b border-white/10">
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-yellow/70 to-transparent pointer-events-none" />
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-brand-yellow/10 border border-brand-yellow/30 flex items-center justify-center flex-shrink-0">
              <Calendar size={16} className="text-brand-yellow" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand-yellow">Reserve · Powered by Acuity</div>
              <div data-testid="booking-modal-title" className="font-display text-base sm:text-lg text-white tracking-wide truncate">{title}</div>
              {subtitle && <div className="text-xs text-zinc-400 truncate mt-0.5">{subtitle}</div>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {src && (
              <a
                href={src}
                target="_blank"
                rel="noreferrer"
                data-testid="booking-modal-open-tab"
                className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/15 bg-white/5 text-[10px] uppercase tracking-[0.25em] text-zinc-300 hover:text-brand-yellow hover:border-brand-yellow/40 transition-colors"
                aria-label="Open booking in a new tab"
              >
                Open in tab <ExternalLink size={11} />
              </a>
            )}
            <button
              type="button"
              onClick={handleClose}
              data-testid="booking-modal-close"
              aria-label="Close booking"
              className="w-9 h-9 rounded-full bg-white/5 border border-white/15 flex items-center justify-center text-zinc-300 hover:text-white hover:bg-white/15 transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="relative flex-1 bg-white">
          {src ? (
            <>
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <Loader2 className="text-zinc-300 animate-spin" size={28} />
              </div>
              <iframe
                data-testid="booking-modal-iframe"
                src={src}
                title="USDD Acuity booking"
                width="100%"
                height="100%"
                frameBorder="0"
                className="relative w-full h-full"
                style={{ height: "70vh", minHeight: 480, maxHeight: "75vh" }}
                allow="payment"
              />
            </>
          ) : (
            <div className="p-8 text-center bg-black text-zinc-300">
              <p>Booking system not configured yet. Please contact us directly.</p>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
